/**
 * CLI Authorization Page
 *
 * Displayed when `quickmask login` opens the browser.
 * If the user is not logged in, redirects to /login with a return path.
 * If logged in, shows a consent screen and, on approval, redirects
 * the Supabase session tokens back to the CLI's local callback server.
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Shield, FileText, CreditCard, Download, Loader2, Terminal, XCircle } from 'lucide-react';
import ShieldIcon from '../ShieldIcon';

const PERMISSIONS = [
    { icon: FileText, label: 'Upload and process PDF documents' },
    { icon: CreditCard, label: 'Use your credit balance' },
    { icon: Download, label: 'Download redacted files' },
];

const CLIAuthorizePage: React.FC = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { user, session, isAuthenticated, isLoading } = useAuth();

    const [isAuthorizing, setIsAuthorizing] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [done, setDone] = useState(false);

    const redirectUri = searchParams.get('redirect_uri');

    // If not authenticated, redirect to login with return path in URL query
    useEffect(() => {
        if (!isLoading && !isAuthenticated) {
            const returnPath = `/cli/authorize?redirect_uri=${encodeURIComponent(redirectUri || '')}`;
            navigate(`/login?next=${encodeURIComponent(returnPath)}`, { replace: true });
        }
    }, [isAuthenticated, isLoading, navigate, redirectUri]);

    if (isLoading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-secondary-50 via-white to-brand-100/35 dark:from-secondary-900 dark:via-secondary-900 dark:to-brand-900/25">
                <Loader2 className="h-8 w-8 animate-spin text-secondary-400" />
            </div>
        );
    }

    if (!redirectUri) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-secondary-50 via-white to-brand-100/35 dark:from-secondary-900 dark:via-secondary-900 dark:to-brand-900/25 px-4">
                <div className="max-w-md w-full bg-white dark:bg-secondary-800 rounded-2xl shadow-xl p-8 text-center">
                    <XCircle className="h-12 w-12 text-danger-600 dark:text-danger-400 mx-auto mb-4" aria-hidden />
                    <h2 className="text-xl font-bold text-secondary-800 dark:text-secondary-100 mb-2">Invalid Request</h2>
                    <p className="text-secondary-600 dark:text-secondary-400">
                        Missing redirect URI. Please use <code className="bg-secondary-100 dark:bg-secondary-700 px-1.5 py-0.5 rounded text-sm">quickmask login</code> from the terminal.
                    </p>
                </div>
            </div>
        );
    }

    const handleAuthorize = () => {
        if (!session?.access_token) {
            setError('No active session. Please log in again.');
            return;
        }

        setIsAuthorizing(true);
        setError(null);

        try {
            const params = new URLSearchParams({
                access_token: session.access_token,
                refresh_token: session.refresh_token || '',
            });
            window.location.href = `${redirectUri}?${params.toString()}`;
            setDone(true);
        } catch (err) {
            setError('Failed to authorize. Please try again.');
            setIsAuthorizing(false);
        }
    };

    const handleDeny = () => {
        if (redirectUri) {
            window.location.href = `${redirectUri}?error=access_denied&error_description=User+denied+access`;
        } else {
            navigate('/');
        }
    };

    if (done) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-secondary-50 via-white to-brand-100/35 dark:from-secondary-900 dark:via-secondary-900 dark:to-brand-900/25 px-4">
                <div className="max-w-md w-full bg-white dark:bg-secondary-800 rounded-2xl shadow-xl p-8 text-center">
                    <Shield className="h-12 w-12 text-green-500 mx-auto mb-4" />
                    <h2 className="text-xl font-bold text-secondary-800 dark:text-secondary-100 mb-2">Authorized</h2>
                    <p className="text-secondary-600 dark:text-secondary-400">
                        You can close this tab and return to the terminal.
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-secondary-50 via-white to-brand-100/35 dark:from-secondary-900 dark:via-secondary-900 dark:to-brand-900/25 py-12 px-4">
            <div className="max-w-md w-full">
                {/* Header */}
                <div className="text-center mb-8">
                    <div className="inline-flex items-center justify-center p-3 rounded-2xl bg-white dark:bg-secondary-800 shadow-lg">
                        <ShieldIcon className="h-12 w-12" />
                    </div>
                </div>

                {/* Authorization Card */}
                <div className="bg-white dark:bg-secondary-800 rounded-2xl shadow-xl p-8">
                    {/* CLI badge */}
                    <div className="flex items-center justify-center mb-6">
                        <div className="flex items-center gap-2 px-4 py-2 bg-secondary-100 dark:bg-secondary-700 rounded-full">
                            <Terminal className="h-4 w-4 text-secondary-600 dark:text-secondary-300" />
                            <span className="text-sm font-medium text-secondary-700 dark:text-secondary-300">
                                QuickMask CLI
                            </span>
                        </div>
                    </div>

                    <h2 className="text-xl font-bold text-center text-secondary-800 dark:text-secondary-100 mb-2">
                        Authorize CLI Access
                    </h2>
                    <p className="text-center text-sm text-secondary-600 dark:text-secondary-400 mb-6">
                        QuickMask CLI is requesting access to your account
                    </p>

                    {/* Permissions list */}
                    <div className="space-y-3 mb-6">
                        {PERMISSIONS.map(({ icon: Icon, label }) => (
                            <div
                                key={label}
                                className="flex items-center gap-3 px-4 py-3 bg-secondary-50 dark:bg-secondary-700/50 rounded-xl"
                            >
                                <div className="flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-lg bg-green-100 dark:bg-green-900/30">
                                    <Icon className="h-4 w-4 text-green-600 dark:text-green-400" />
                                </div>
                                <span className="text-sm text-secondary-700 dark:text-secondary-300">{label}</span>
                            </div>
                        ))}
                    </div>

                    {/* Logged in as */}
                    <div className="text-center text-sm text-secondary-500 dark:text-secondary-400 mb-6 py-3 border-t border-secondary-200 dark:border-secondary-600">
                        Logged in as{' '}
                        <span className="font-medium text-secondary-700 dark:text-secondary-200">
                            {user?.email}
                        </span>
                    </div>

                    {/* Error */}
                    {error && (
                        <div className="mb-4 p-3 bg-danger-50 dark:bg-danger-900/30 border border-danger-200 dark:border-danger-700 rounded-xl text-sm text-danger-800 dark:text-danger-200">
                            {error}
                        </div>
                    )}

                    {/* Buttons */}
                    <div className="space-y-3">
                        <button
                            onClick={handleAuthorize}
                            disabled={isAuthorizing}
                            className="w-full flex items-center justify-center py-3 px-4 rounded-xl text-white bg-brand-500 dark:bg-brand-600 hover:bg-brand-600 dark:hover:bg-brand-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-500 font-medium transition-colors disabled:opacity-50"
                        >
                            {isAuthorizing ? (
                                <>
                                    <Loader2 className="h-5 w-5 animate-spin mr-2" />
                                    Authorizing...
                                </>
                            ) : (
                                'Authorize'
                            )}
                        </button>
                        <button
                            onClick={handleDeny}
                            disabled={isAuthorizing}
                            className="w-full py-3 px-4 rounded-xl border border-secondary-300 dark:border-secondary-600 text-secondary-700 dark:text-secondary-300 hover:bg-secondary-50 dark:hover:bg-secondary-700 font-medium transition-colors disabled:opacity-50"
                        >
                            Deny
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CLIAuthorizePage;
